"use client";

import React, { useEffect, useState } from "react";
import * as z from "zod";
import { LoginSchema } from "@/schemas";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import styles from "./login-form.module.scss";
import StyledInput from "@/components/common/input/styled-input";
import { login } from "@/actions/auth/login";
import SignButton from "@/components/pages/auth/components/sign-button";
import GoogleButton from "@/components/pages/auth/components/google-button";
import HorizontalSpan from "@/components/pages/auth/components/horizontal-span";
import TransparentButton from "@/components/common/transparent-button";
import Link from "next/link";
import ErrorText from "@/components/pages/auth/components/error-text";
import BackHomeButton from "@/components/pages/auth/components/back-home-button";
import { useSearchParams } from "next/navigation";
import { useMobile } from "@/hooks/use-responsive";
import Image from "next/image";
import logo from "@/assets/sidebar/logo.svg";
import { showVerificationEmailSent } from "@/utils/notification-helper";
import { ToastContainer } from "react-toastify";
import DiscordButton from "@/components/pages/auth/components/discord-button";

const LoginForm = () => {
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get("callbackUrl");

  let registerUrl = "/register";
  if (callbackUrl) {
    registerUrl +=
      "?" +
      new URLSearchParams({
        callbackUrl,
      });
  }

  const { isMobile } = useMobile();

  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [success, setSuccess] = useState("");
  const [isPending, setIsPending] = useState(false);

  const form = useForm<z.infer<typeof LoginSchema>>({
    resolver: zodResolver(LoginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof LoginSchema>) => {
    setError("");
    setSuccess("");
    setIsPending(true);

    try {
      const data = await login(values, callbackUrl);

      if (data?.error) {
        form.reset();
        setError(data.error);
      }

      if (data?.success) {
        form.reset();
        setSuccess(data.success);
        showVerificationEmailSent(data.success);
      }
    } catch (error) {
      setError("Something went wrong");
    } finally {
      setIsPending(false);
    }
  };
  const onInvalid = (errors: any) => {
    setEmailError(errors.email?.message);
    setPasswordError(errors.password?.message);
  };

  useEffect(() => {
    const errorType = searchParams.get("error");
    if (errorType) {
      if (errorType == "OAuthAccountNotLinked")
        setError("Another account already exists with the same e-mail address");
    }
  }, [searchParams]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      form.handleSubmit(onSubmit, onInvalid)();
    }
  };

  return (
    <div className={styles.wrapper} onKeyDown={handleKeyDown}>
      {!isMobile && (
        <div className={styles.back_button}>
          <BackHomeButton />
        </div>
      )}
      {isMobile && (
        <div className={styles.logo_container}>
          <Image src={logo} alt="logo" />
        </div>
      )}
      <div className={styles.container}>
        <div className={styles.column}>
          Sign in to AudioModify to continue
          <GoogleButton
            title="Sign in with Google"
            isDisabled={isPending}
            callbackUrl={callbackUrl}
          />
          <DiscordButton
            title="Sign in with Discord"
            callbackUrl={callbackUrl}
            isDisabled={isPending}
          />
          <HorizontalSpan />
          <div className={styles.inputs_column}>
            <div className={styles.input_container}>
              <StyledInput
                title="Enter your email"
                type="email"
                placeholder="Email"
                isDisabled={isPending}
                isValid={!emailError}
                registerField={form.register("email")}
              />
              <ErrorText error={emailError} />
            </div>
            <div className={styles.input_container}>
              <StyledInput
                title="Password"
                type="password"
                placeholder="Password"
                isDisabled={isPending}
                isValid={!passwordError}
                registerField={form.register("password")}
              />
              <ErrorText error={passwordError} />
            </div>
            <div className={styles.row_container}>
              <Link className={styles.forgot_password_text} href={"/reset"}>
                Forgot password?
              </Link>
              <div className={styles.sign_button}>
                <SignButton
                  title="Sign in"
                  isDisabled={isPending}
                  onClick={() => {
                    setError("");
                    setEmailError("");
                    setPasswordError("");
                    form.handleSubmit(onSubmit, onInvalid)();
                  }}
                />
              </div>
            </div>
            {error && <ErrorText error={error} />}
          </div>
        </div>
        <div className={styles.footer}>
          <div className={styles.horizontal_line}></div>
          <div>Don&apos;t have an account?</div>
          <TransparentButton>
            <Link className={styles.link_button} href={registerUrl}>
              Sign up for AudioModify
            </Link>
          </TransparentButton>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default LoginForm;
